import { useState, useEffect, useCallback, useMemo } from "react";

import { Box, Button, Group, Stack, Text, Textarea, TextInput } from "@mantine/core";

import { APIKit } from "config/axios";
import { ServerConstants, TodoConstants } from "constant";

import {
  DateTimeErrors,
  DateTimeFields,
  hasDateTimeErrors,
  validateDateTimeFields,
} from "../helpers";
import { TodoItemData } from "../item";
import DateTimeSelect, { ChevronMultiSelect, ChevronSelect } from "../new/DateTimeSelect";
import PriorityCheckbox from "../new/PriorityCheckbox";

import PriorityBadge from "./PriorityBadge";

const { API_V1_USERS } = ServerConstants;
const { MONTHS } = TodoConstants;

interface UserOption {
  id: number;
  fullName: string;
}

interface UserApiResponse {
  id: number;
  fullName?: string;
  firstName?: string;
  lastName?: string;
}

const TYPE_MAP: Record<string, "CALL" | "MEETING" | "INTERNAL_TASK"> = {
  Poziv: "CALL",
  Sastanak: "MEETING",
  "Interni zadatak": "INTERNAL_TASK",
};

const TODO_TYPE_OPTIONS = ["Poziv", "Sastanak", "Interni zadatak"] as const;

const getTodoTypeLabel = (type: TodoItemData["type"]): string => {
  switch (type) {
    case "CALL":
      return "Poziv";
    case "MEETING":
      return "Sastanak";
    case "INTERNAL_TASK":
      return "Interni zadatak";
  }
};

interface TodoDetailProps {
  todo: TodoItemData | null;
  onSave: (todo: TodoItemData) => void;
  onDelete: (id: number) => void;
  onCancel: () => void;
  readOnly?: boolean;
}

const emptyDateTime: DateTimeFields = { day: null, month: null, year: null, time: "" };

// Parse ISO date string to form fields
const parseDateTime = (dateString?: string): DateTimeFields => {
  if (!dateString) return emptyDateTime;
  const date = new Date(dateString);
  return {
    day: String(date.getDate()),
    month: MONTHS[date.getMonth()],
    year: String(date.getFullYear()),
    time: `${date.getHours().toString().padStart(2, "0")}:${date.getMinutes().toString().padStart(2, "0")}`,
  };
};

// Format date fields to display string
const formatFromFields = (dt: DateTimeFields): string => {
  if (!dt.day || !dt.month || !dt.year) return "";
  const day = dt.day.padStart(2, "0");
  const month = String(MONTHS.indexOf(dt.month) + 1).padStart(2, "0");
  return `${day}/${month}/${dt.year}${dt.time ? ` ${dt.time}` : ""}`;
};

interface FormState {
  title: string;
  description: string;
  todoType: string | null;
  assignedUserIds: string[];
  urgent: boolean;
  important: boolean;
  dateTime: DateTimeFields;
  endDateTime: DateTimeFields;
}

const TodoDetail = ({ todo, onSave, onDelete, onCancel, readOnly = false }: TodoDetailProps) => {
  const isArchived = todo?.archive === true;
  const isDisabled = isArchived || readOnly;

  const [formState, setFormState] = useState<FormState>({
    title: "",
    description: "",
    todoType: null,
    assignedUserIds: [],
    urgent: false,
    important: false,
    dateTime: emptyDateTime,
    endDateTime: emptyDateTime,
  });
  const [employeeOptions, setEmployeeOptions] = useState<UserOption[]>([]);
  const [dateErrors, setDateErrors] = useState<DateTimeErrors>({});

  // Fetch employees for dropdown
  useEffect(() => {
    const fetchEmployees = async () => {
      try {
        const response = await APIKit.get(`${API_V1_USERS}?type=EMPLOYEE`);
        const data: UserApiResponse[] = response.data?.content || response.data || [];
        setEmployeeOptions(
          data.map((u) => ({
            id: u.id,
            fullName: u.fullName || `${u.firstName || ""} ${u.lastName || ""}`.trim() || "Unknown",
          })),
        );
      } catch (error) {
        console.error("Error fetching employees:", error);
      }
    };
    fetchEmployees();
  }, []);

  useEffect(() => {
    if (todo) {
      const primaryDate = todo.dueDate || todo.startTime;
      setFormState({
        title: todo.title,
        description: todo.description || "",
        todoType: getTodoTypeLabel(todo.type),
        assignedUserIds: todo.users?.map((u) => String(u.id)) || [],
        urgent: todo.urgent,
        important: todo.important,
        dateTime: parseDateTime(primaryDate),
        endDateTime: parseDateTime(todo.endTime),
      });
      setDateErrors({});
    }
  }, [todo]);

  const displayDateTime = useMemo(() => {
    if (!todo) return "";

    const startFormatted = formatFromFields(formState.dateTime);
    if (!startFormatted) return "";

    if (todo.timeType === "INTERVAL") {
      const endFormatted = formatFromFields(formState.endDateTime);
      if (!endFormatted) return startFormatted;

      const sameDate =
        formState.dateTime.day === formState.endDateTime.day &&
        formState.dateTime.month === formState.endDateTime.month &&
        formState.dateTime.year === formState.endDateTime.year;

      return sameDate
        ? `${startFormatted} - ${formState.endDateTime.time}`
        : `${startFormatted} - ${endFormatted}`;
    }

    return startFormatted;
  }, [todo, formState.dateTime, formState.endDateTime]);

  const employeeSelectOptions = useMemo(
    () => employeeOptions.map((u) => ({ value: String(u.id), label: u.fullName })),
    [employeeOptions],
  );

  // Build ISO datetime string from form fields
  const buildDateTime = useCallback((dt: DateTimeFields): string | undefined => {
    if (!dt.day || !dt.month || !dt.year) return undefined;
    const monthIndex = MONTHS.indexOf(dt.month);
    const [hours, minutes] = dt.time ? dt.time.split(":").map(Number) : [0, 0];
    const year = dt.year;
    const month = String(monthIndex + 1).padStart(2, "0");
    const day = String(Number(dt.day)).padStart(2, "0");
    const hour = String(hours || 0).padStart(2, "0");
    const min = String(minutes || 0).padStart(2, "0");
    return `${year}-${month}-${day}T${hour}:${min}:00`;
  }, []);

  // Re-validate interval and update errors (clears all end errors if valid)
  const revalidateInterval = useCallback((startDT: DateTimeFields, endDT: DateTimeFields) => {
    const errors = validateDateTimeFields(startDT, endDT, true);
    // Only update end-related errors, keep start errors as they were
    // Explicitly set false when no error (errors.X is undefined)
    setDateErrors((prev) => ({
      ...prev,
      endDay: errors.endDay ?? false,
      endMonth: errors.endMonth ?? false,
      endYear: errors.endYear ?? false,
      endTime: errors.endTime ?? false,
    }));
  }, []);

  const handleSave = useCallback(() => {
    if (!todo) return;

    // Validate date/time fields
    const isInterval = todo.timeType === "INTERVAL";
    const errors = validateDateTimeFields(
      formState.dateTime,
      isInterval ? formState.endDateTime : undefined,
      isInterval,
    );

    if (hasDateTimeErrors(errors)) {
      setDateErrors(errors);
      return;
    }

    const users = formState.assignedUserIds.map((id) => {
      const employee = employeeOptions.find((e) => String(e.id) === id);
      return {
        id: Number(id),
        fullName: employee?.fullName || "",
        email: "",
        type: "EMPLOYEE" as const,
      };
    });

    const updatedTodo: TodoItemData = {
      ...todo,
      title: formState.title,
      description: formState.description,
      type: formState.todoType ? TYPE_MAP[formState.todoType] : todo.type,
      urgent: formState.urgent,
      important: formState.important,
      users,
    };

    // Update date fields based on timeType
    const primaryDateTime = buildDateTime(formState.dateTime);
    if (todo.timeType === "DEADLINE") {
      updatedTodo.dueDate = primaryDateTime;
    } else {
      updatedTodo.startTime = primaryDateTime;
      if (todo.timeType === "INTERVAL") {
        updatedTodo.endTime = buildDateTime(formState.endDateTime);
      }
    }

    onSave(updatedTodo);
  }, [formState, employeeOptions, todo, onSave, buildDateTime]);

  if (!todo) {
    return (
      <Box
        p="xl"
        h="100%"
        style={{ display: "flex", alignItems: "center", justifyContent: "center" }}
      >
        <Text c="dimmed" fz={14}>
          Izaberite zadatak da vidite detalje
        </Text>
      </Box>
    );
  }

  return (
    <Stack h="100%" justify="space-between">
      <Stack gap={0}>
        {/* Header section with gray background */}
        <Box p="lg" bg="gray.1">
          <Stack gap="md">
            <Group justify="space-between" align="flex-start">
              <Text fz={13} c="dimmed">
                {displayDateTime}
              </Text>
              <Group gap="xs">
                {formState.urgent && <PriorityBadge label="Hitno" color="red" />}
                {formState.important && <PriorityBadge label="Važno" color="orange" />}
              </Group>
            </Group>

            {/* Title */}
            <TextInput
              placeholder="Tekst To Do-a"
              variant="unstyled"
              value={formState.title}
              onChange={(e) => setFormState((prev) => ({ ...prev, title: e.target.value }))}
              disabled={isDisabled}
              styles={{
                input: {
                  fontSize: 20,
                  fontWeight: "bold",
                },
              }}
            />

            {/* Description */}
            <Textarea
              placeholder="Detaljniji opis to do-a"
              variant="unstyled"
              autosize
              minRows={2}
              value={formState.description}
              onChange={(e) => setFormState((prev) => ({ ...prev, description: e.target.value }))}
              disabled={isDisabled}
              styles={{
                input: {
                  fontSize: 14,
                  color: "var(--mantine-color-gray-6)",
                  border: "1px solid var(--mantine-color-gray-3)",
                  borderRadius: "var(--mantine-radius-md)",
                  padding: 12,
                },
              }}
            />
          </Stack>
        </Box>

        <Stack gap="md" p="lg">
          {/* Category */}
          <Group wrap="nowrap">
            <Text fz={14} c="dimmed" w={80}>
              Kategorija:
            </Text>
            <ChevronSelect
              placeholder="Izaberi"
              data={TODO_TYPE_OPTIONS}
              value={formState.todoType}
              onChange={(value) => setFormState((prev) => ({ ...prev, todoType: value }))}
              radius="md"
              size="sm"
              flex={1}
              disabled={isDisabled}
            />
          </Group>

          {/* Created by */}
          <Group wrap="nowrap">
            <Text fz={14} c="dimmed" w={80}>
              Od:
            </Text>
            <Text fz={14} fw={500}>
              {todo.createdBy?.fullName || "Ja"}
            </Text>
          </Group>

          {/* Assigned to */}
          <Group wrap="nowrap">
            <Text fz={14} c="dimmed" w={80}>
              Za:
            </Text>
            <ChevronMultiSelect
              placeholder="Izaberi"
              data={employeeSelectOptions}
              value={formState.assignedUserIds}
              onChange={(value) => setFormState((prev) => ({ ...prev, assignedUserIds: value }))}
              radius="md"
              size="sm"
              flex={1}
              disabled={isDisabled}
            />
          </Group>

          {/* Date/Time */}
          <Group wrap="nowrap" align="flex-start">
            <Text fz={14} c="dimmed" w={80} mt={6}>
              {todo.timeType === "INTERVAL" ? "Početak:" : "Datum:"}
            </Text>
            <DateTimeSelect
              day={formState.dateTime.day}
              month={formState.dateTime.month}
              year={formState.dateTime.year}
              time={formState.dateTime.time}
              onDayChange={(day) => {
                const newDateTime = { ...formState.dateTime, day };
                setFormState((prev) => ({ ...prev, dateTime: newDateTime }));
                setDateErrors((prev) => ({ ...prev, day: false }));
                if (todo.timeType === "INTERVAL")
                  revalidateInterval(newDateTime, formState.endDateTime);
              }}
              onMonthChange={(month) => {
                const newDateTime = { ...formState.dateTime, month };
                setFormState((prev) => ({ ...prev, dateTime: newDateTime }));
                setDateErrors((prev) => ({ ...prev, month: false }));
                if (todo.timeType === "INTERVAL")
                  revalidateInterval(newDateTime, formState.endDateTime);
              }}
              onYearChange={(year) => {
                const newDateTime = { ...formState.dateTime, year };
                setFormState((prev) => ({ ...prev, dateTime: newDateTime }));
                setDateErrors((prev) => ({ ...prev, year: false }));
                if (todo.timeType === "INTERVAL")
                  revalidateInterval(newDateTime, formState.endDateTime);
              }}
              onTimeChange={(time) => {
                const newDateTime = { ...formState.dateTime, time };
                setFormState((prev) => ({ ...prev, dateTime: newDateTime }));
                setDateErrors((prev) => ({ ...prev, time: false }));
                if (todo.timeType === "INTERVAL")
                  revalidateInterval(newDateTime, formState.endDateTime);
              }}
              dayError={dateErrors.day}
              monthError={dateErrors.month}
              yearError={dateErrors.year}
              timeError={dateErrors.time}
              disabled={isDisabled}
            />
          </Group>

          {todo.timeType === "INTERVAL" && (
            <Group wrap="nowrap" align="flex-start">
              <Text fz={14} c="dimmed" w={80} mt={6}>
                Kraj:
              </Text>
              <DateTimeSelect
                day={formState.endDateTime.day}
                month={formState.endDateTime.month}
                year={formState.endDateTime.year}
                time={formState.endDateTime.time}
                onDayChange={(day) => {
                  const newEndDateTime = { ...formState.endDateTime, day };
                  setFormState((prev) => ({ ...prev, endDateTime: newEndDateTime }));
                  revalidateInterval(formState.dateTime, newEndDateTime);
                }}
                onMonthChange={(month) => {
                  const newEndDateTime = { ...formState.endDateTime, month };
                  setFormState((prev) => ({ ...prev, endDateTime: newEndDateTime }));
                  revalidateInterval(formState.dateTime, newEndDateTime);
                }}
                onYearChange={(year) => {
                  const newEndDateTime = { ...formState.endDateTime, year };
                  setFormState((prev) => ({ ...prev, endDateTime: newEndDateTime }));
                  revalidateInterval(formState.dateTime, newEndDateTime);
                }}
                onTimeChange={(time) => {
                  const newEndDateTime = { ...formState.endDateTime, time };
                  setFormState((prev) => ({ ...prev, endDateTime: newEndDateTime }));
                  revalidateInterval(formState.dateTime, newEndDateTime);
                }}
                dayError={dateErrors.endDay}
                monthError={dateErrors.endMonth}
                yearError={dateErrors.endYear}
                timeError={dateErrors.endTime}
                disabled={isDisabled}
              />
            </Group>
          )}

          {/* Priority checkboxes */}
          <Group gap="md" mt="sm">
            <PriorityCheckbox
              label="Hitno"
              checked={formState.urgent}
              onChange={(value) => setFormState((prev) => ({ ...prev, urgent: value }))}
              disabled={isDisabled}
            />
            <PriorityCheckbox
              label="Važno"
              checked={formState.important}
              onChange={(value) => setFormState((prev) => ({ ...prev, important: value }))}
              disabled={isDisabled}
            />
          </Group>
        </Stack>
      </Stack>

      {/* Footer buttons */}
      <Group justify={isDisabled ? "flex-end" : "space-between"} p="lg">
        {!isDisabled && (
          <Button
            variant="outline"
            color="red"
            radius="md"
            size="sm"
            fz={14}
            onClick={() => onDelete(todo.id)}
          >
            Izbriši To Do
          </Button>
        )}
        <Group gap="md">
          <Button variant="outline" color="dark" radius="md" size="sm" fz={14} onClick={onCancel}>
            Otkaži
          </Button>
          {!isDisabled && (
            <Button color="green" radius="md" size="sm" fz={14} onClick={handleSave}>
              Sačuvaj
            </Button>
          )}
        </Group>
      </Group>
    </Stack>
  );
};

export default TodoDetail;
